import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { adminService, SystemTask, SystemTasksSummary } from '../services/adminService';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import SelectDropdown from '../components/SelectDropdown';
import ConfirmDialog from '../components/ConfirmDialog';
import { SystemTaskTypeFilter } from '@/services/enums';

type QueueTypeFilter =
  | SystemTaskTypeFilter;
type StatusFilter = 'all' | 'pending' | 'running' | 'completed' | 'failed';

const SystemTasks: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [allTasks, setAllTasks] = useState<SystemTask[]>([]); // 存储所有任务
  const [selectedQueueType, setSelectedQueueType] = useState<QueueTypeFilter>('all');
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('all');
  const [summary, setSummary] = useState<SystemTasksSummary | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    queueType: string;
    taskId: string;
    taskName: string;
  }>({
    show: false,
    queueType: '',
    taskId: '',
    taskName: '',
  });
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [taskResultDialog, setTaskResultDialog] = useState<{
    show: boolean;
    task: SystemTask | null;
  }>({
    show: false,
    task: null,
  });
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = useRef(false);

  // 根据选中的类型和状态过滤任务
  const tasks = React.useMemo(() => {
    let filtered = allTasks;

    // 先按类型过滤
    if (selectedQueueType !== 'all') {
      filtered = filtered.filter((task) => task.queueType === selectedQueueType);
    }

    // 再按状态过滤
    if (selectedStatus !== 'all') {
      filtered = filtered.filter((task) => task.status === selectedStatus);
    }

    return filtered;
  }, [allTasks, selectedQueueType, selectedStatus]);

  const fetchTasks = async () => {
    try {
      const response = await adminService.getSystemTasks();
      if (response) {
        setAllTasks(response.tasks);
        setSummary(response.summary);
      } else {
        showToast('获取系统任务失败', 'error');
      }
    } catch (error: any) {
      console.error('获取系统任务失败:', error);
      showToast(error.message || '获取系统任务失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 只在首次挂载时加载，避免重复请求
    if (hasLoadedRef.current) {
      return;
    }

    const loadTasks = async () => {
      hasLoadedRef.current = true;
      setLoading(true);
      await fetchTasks();
    };

    loadTasks();

    // 设置自动刷新（每10秒刷新一次）
    refreshIntervalRef.current = setInterval(() => {
      fetchTasks();
    }, 10000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
      case 'running':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
      case 'completed':
        return 'bg-green-500/20 text-green-300 border-green-500/50';
      case 'failed':
        return 'bg-red-500/20 text-red-300 border-red-500/50';
      default:
        return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return '等待中';
      case 'running':
        return '运行中';
      case 'completed':
        return '已完成';
      case 'failed':
        return '失败';
      default:
        return status;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchTasks();
  };

  const handleDeleteTask = (queueType: string, taskId: string, taskName: string) => {
    setConfirmDialog({
      show: true,
      queueType,
      taskId,
      taskName,
    });
  };

  const confirmDeleteTask = async () => {
    const { queueType, taskId } = confirmDialog;
    setDeletingTaskId(taskId);

    try {
      await adminService.deleteSystemTask(queueType, taskId);
      showToast('任务已删除', 'success');
      // 刷新任务列表
      await fetchTasks();
    } catch (error: any) {
      console.error('删除任务失败:', error);
      showToast(error.message || '删除任务失败', 'error');
    } finally {
      setDeletingTaskId(null);
      setConfirmDialog({ show: false, queueType: '', taskId: '', taskName: '' });
    }
  };

  const cancelDeleteTask = () => {
    setConfirmDialog({ show: false, queueType: '', taskId: '', taskName: '' });
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader
          title="系统任务"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="group flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105 disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {/* 统计摘要 */}
          {summary && (
            <div className="mb-6 space-y-4">
              {/* 按队列类型统计 */}
              <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-white/90 text-base font-semibold">任务统计</h2>
                  <span className="text-white/60 text-sm">总计: {summary.total}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">提示词分析</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['kline-prompt'] || 0}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">监控规则分析</div>
                    <div className="text-white text-lg font-bold">
                      {summary.byQueueType['kline-monitor-rules'] || 0}
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">订单任务</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['order'] || 0}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">币种分析</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['coin-analysis'] || 0}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">价格监控</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['price-monitor'] || 0}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">股票情报收集</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['stock-collect'] || 0}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">股票 AI 分析</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['stock-analyze'] || 0}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">信号回测</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['signal-backtest'] || 0}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-white/60 text-xs mb-1">信号优化</div>
                    <div className="text-white text-lg font-bold">{summary.byQueueType['signal-optimize'] || 0}</div>
                  </div>
                </div>
              </div>

              {/* 按状态统计 */}
              {summary.byStatus && (
                <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                  <h2 className="text-white/90 text-base font-semibold mb-3">状态统计</h2>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-white/60 text-xs mb-1">等待中</div>
                      <div className="text-yellow-300 text-lg font-bold">{summary.byStatus.pending}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-white/60 text-xs mb-1">运行中</div>
                      <div className="text-blue-300 text-lg font-bold">{summary.byStatus.running}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-white/60 text-xs mb-1">已完成</div>
                      <div className="text-green-300 text-lg font-bold">{summary.byStatus.completed}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-white/60 text-xs mb-1">失败</div>
                      <div className="text-red-300 text-lg font-bold">{summary.byStatus.failed}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 筛选条件 */}
          <div className="mb-4">
            <div className="bg-white/5 border border-white/20 rounded-xl p-4">
              <div className="space-y-3">
                {/* 任务类型筛选 */}
                <div className="flex items-center gap-3">
                  <label className="text-white/90 text-sm font-medium whitespace-nowrap">任务类型筛选</label>
                  <SelectDropdown
                    value={selectedQueueType}
                    onChange={(val) => setSelectedQueueType(val as QueueTypeFilter)}
                    options={[
                      { value: 'all', label: '全部任务' },
                      { value: 'kline-prompt', label: '提示词分析' },
                      { value: 'kline-monitor-rules', label: '监控规则分析' },
                      { value: 'order', label: '订单任务' },
                      { value: 'coin-analysis', label: '币种分析' },
                      { value: 'price-monitor', label: '价格监控' },
                      { value: 'stock-collect', label: '股票情报收集' },
                      { value: 'stock-analyze', label: '股票 AI 分析' },
                      { value: 'signal-backtest', label: '信号回测' },
                      { value: 'signal-optimize', label: '信号优化' },
                      { value: 'batch-backtest', label: '批量回测' },
                      { value: 'batch-optimize', label: '批量优化' },
                    ]}
                    className="flex-1 mb-0"
                  />
                </div>

                {/* 任务状态筛选 */}
                <div className="flex items-center gap-3">
                  <label className="text-white/90 text-sm font-medium whitespace-nowrap">任务状态筛选</label>
                  <SelectDropdown
                    value={selectedStatus}
                    onChange={(val) => setSelectedStatus(val as StatusFilter)}
                    options={[
                      { value: 'all', label: '全部状态' },
                      { value: 'pending', label: '等待中' },
                      { value: 'running', label: '运行中' },
                      { value: 'completed', label: '已完成' },
                      { value: 'failed', label: '失败' },
                    ]}
                    className="flex-1 mb-0"
                  />
                </div>
              </div>
              {(selectedQueueType !== 'all' || selectedStatus !== 'all') && (
                <div className="mt-2 text-white/60 text-xs">
                  显示 {tasks.length} 个任务（共 {allTasks.length} 个）
                </div>
              )}
            </div>
          </div>

          {/* 加载状态 */}
          {loading && tasks.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-white/20 border-t-white/80 rounded-full animate-spin"></div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-white/40 text-sm">暂无系统任务</div>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task, index) => (
                <div
                  key={`${task.queueType}-${task.taskId}-${index}`}
                  className="bg-white/5 border border-white/20 rounded-xl p-4 text-white/90 cursor-pointer hover:bg-white/10 transition-colors"
                  onClick={() => setTaskResultDialog({ show: true, task })}
                >
                  {/* 第一行：任务类型和状态 */}
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                      <span className="text-white font-semibold whitespace-nowrap flex-shrink-0">{task.queueName}</span>
                      {task.symbol && (
                        <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 whitespace-nowrap overflow-hidden text-ellipsis block">
                          {task.symbol}
                        </span>
                      )}
                      {task.bar && (
                        <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 whitespace-nowrap flex-shrink-0">
                          {task.bar}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-1 rounded text-xs border ${getStatusColor(task.status)}`}>
                        {getStatusLabel(task.status)}
                      </span>
                    </div>
                  </div>

                  {/* 第二行：任务ID和错误信息 */}
                  <div className="text-xs text-white/50 mb-2">
                    任务ID: {task.taskId}
                    {task.error && <div className="text-red-400 mt-1 break-words">错误: {task.error}</div>}
                  </div>

                  {/* 第三行：时间信息 */}
                  <div className="flex items-end justify-between text-xs text-white/60">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="text-white/40 mb-1">创建时间</div>
                        <div>{formatDate(task.createdAt)}</div>
                      </div>
                      {task.finishedAt && (
                        <div>
                          <div className="text-white/40 mb-1">完成时间</div>
                          <div>{formatDate(task.finishedAt)}</div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // 阻止事件冒泡，避免触发任务详情对话框
                        handleDeleteTask(task.queueType, task.taskId, task.queueName);
                      }}
                      disabled={deletingTaskId === task.taskId}
                      className="px-2 py-1 bg-red-500/20 text-red-300 border border-red-500/50 rounded text-xs hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      title="删除任务"
                    >
                      {deletingTaskId === task.taskId ? (
                        <>
                          <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>删除中</span>
                        </>
                      ) : (
                        '删除'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 确认删除对话框 */}
        <ConfirmDialog
          show={confirmDialog.show}
          title="确认删除任务"
          message={`确定要删除任务 "${confirmDialog.taskName}" (ID: ${confirmDialog.taskId.substring(0, 8)}...) 吗？此操作不可撤销。`}
          confirmText="删除"
          cancelText="取消"
          loading={deletingTaskId === confirmDialog.taskId}
          onConfirm={confirmDeleteTask}
          onCancel={cancelDeleteTask}
        />

        {/* 任务结果对话框 */}
        {taskResultDialog.show && taskResultDialog.task && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-gray-900 border border-white/20 rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white text-xl font-semibold">任务结果</h2>
                <button
                  onClick={() => setTaskResultDialog({ show: false, task: null })}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4 space-y-2 text-sm text-white/70">
                <div>
                  <span className="text-white/50">任务类型:</span> {taskResultDialog.task.queueName}
                </div>
                <div>
                  <span className="text-white/50">任务ID:</span> {taskResultDialog.task.taskId}
                </div>
                {taskResultDialog.task.symbol && (
                  <div>
                    <span className="text-white/50">币种:</span> {taskResultDialog.task.symbol}
                  </div>
                )}
                {taskResultDialog.task.bar && (
                  <div>
                    <span className="text-white/50">时间粒度:</span> {taskResultDialog.task.bar}
                  </div>
                )}
                <div>
                  <span className="text-white/50">状态:</span> {getStatusLabel(taskResultDialog.task.status)}
                </div>
                {taskResultDialog.task.error && (
                  <div>
                    <span className="text-white/50">错误:</span>{' '}
                    <span className="text-red-400">{taskResultDialog.task.error}</span>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-auto">
                <div className="text-white/50 text-sm mb-2">任务数据:</div>
                <pre className="bg-black/30 border border-white/10 rounded-lg p-4 text-xs text-white/80 overflow-auto max-h-[60vh]">
                  {JSON.stringify(taskResultDialog.task.data || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </GradientBackground>
  );
};

export default SystemTasks;
